import {
  CopyableValue,
  Field,
  FieldGrid,
  Section,
} from '../../../components/common/detailParts';
import { truncateHash } from '../../../components/common/truncateHash';
import { cn } from '../../../lib/utils';
import {
  Chip,
  Empty,
  EmptyState,
  List,
  ListRow,
} from '../../../components/common/detailListParts';
import type { SuperDappDetailData, SuperDappTabProps } from '../types';

type FileMetadata = SuperDappDetailData['files'][number];
type ExternalSyncState = SuperDappDetailData['externalSyncState'][number];

/** Raw record, provider health and stored files — the admin's debugging view. */
export function SystemTab({
  data,
  formatDate,
  formatBytes,
}: SuperDappTabProps) {
  const { files, dapp, externalSyncState } = data;

  return (
    <div className="space-y-4">
      <Section title="Database record">
        <FieldGrid>
          <Field label="Dapp ID">
            <CopyableValue value={dapp.id} />
          </Field>
          <Field label="Workflow status">
            <span className="text-neutral-300">{dapp.workflowStatus}</span>
          </Field>
          <Field label="Display status">
            <span className="text-neutral-300">{dapp.status}</span>
          </Field>
          <Field label="Last approved release">
            {dapp.lastApprovedReleaseId ? (
              <CopyableValue value={dapp.lastApprovedReleaseId} />
            ) : (
              <Empty />
            )}
          </Field>
          <Field label="Created">
            <span className="text-neutral-300">{formatDate(dapp.created)}</span>
          </Field>
          <Field label="Updated">
            <span className="text-neutral-300">{formatDate(dapp.updated)}</span>
          </Field>
          <Field label="Deleted">
            <span
              className={dapp.deleted ? 'text-rose-300' : 'text-neutral-300'}
            >
              {formatDate(dapp.deleted)}
            </span>
          </Field>
        </FieldGrid>
      </Section>

      <Section
        title="Sync providers"
        trailing={
          <span className="text-xs text-neutral-500">
            Global health, not specific to this dapp
          </span>
        }
      >
        {externalSyncState.length === 0 ? (
          <EmptyState message="No sync providers configured" />
        ) : (
          <List>
            {externalSyncState.map((state) => (
              <ExternalSyncRow
                key={state.provider}
                state={state}
                formatDate={formatDate}
              />
            ))}
          </List>
        )}
      </Section>

      <Section
        title="Stored files"
        trailing={
          <span className="text-xs tabular-nums text-neutral-500">
            {files.length}
          </span>
        }
      >
        {files.length === 0 ? (
          <EmptyState message="No file metadata recorded" />
        ) : (
          <List>
            {files.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                formatDate={formatDate}
                formatBytes={formatBytes}
              />
            ))}
          </List>
        )}
      </Section>
    </div>
  );
}

function ExternalSyncRow({
  state,
  formatDate,
}: {
  state: ExternalSyncState;
  formatDate: SuperDappTabProps['formatDate'];
}) {
  const isHealthy = state.lastSyncStatus === 'success';
  const toneClass = state.lastSyncStatus
    ? isHealthy
      ? 'text-teal-400'
      : 'text-rose-400'
    : 'text-neutral-500';

  return (
    <ListRow
      title={
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              state.lastSyncStatus
                ? isHealthy
                  ? 'bg-teal-400'
                  : 'bg-rose-400'
                : 'bg-neutral-600',
            )}
          />
          <span className="font-medium capitalize">{state.provider}</span>
        </span>
      }
      meta={`Last sync ${formatDate(state.lastSyncTimestamp)}`}
      trailing={
        <span className={cn('capitalize', toneClass)}>
          {state.lastSyncStatus || 'Unknown'}
        </span>
      }
    >
      {state.errorMessage && (
        <div className="mt-1.5 text-xs text-rose-300/80">
          {state.errorMessage}
        </div>
      )}
    </ListRow>
  );
}

function FileRow({
  file,
  formatDate,
  formatBytes,
}: {
  file: FileMetadata;
  formatDate: SuperDappTabProps['formatDate'];
  formatBytes: SuperDappTabProps['formatBytes'];
}) {
  return (
    <ListRow
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Chip>{file.fileType}</Chip>
          <span
            className="font-mono text-xs text-neutral-300"
            title={file.fileHash}
          >
            {truncateHash(file.fileHash, 10, 6)}
          </span>
        </span>
      }
      meta={
        <>
          {file.mimeType} · {formatBytes(file.fileSize)}
          {file.facilitator && ` · via ${file.facilitator}`}
        </>
      }
      trailing={
        <div className="flex flex-col items-end gap-0.5">
          {file.providerUri ? (
            <a
              href={file.providerUri}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-400 transition-colors hover:text-teal-300"
            >
              {file.provider}
            </a>
          ) : (
            <span className="text-neutral-400">{file.provider}</span>
          )}
          <span className="text-[10.5px] text-neutral-600">
            {formatDate(file.uploadedAt)}
          </span>
        </div>
      }
    />
  );
}
